import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import useClearAndCloseRowsSelection from "app/pages/sales-intelligence/hooks/useClearAndCloseRowsSelection";
import AssignToOtherUsersPanelView from "app/pages/sales-intelligence/sub-modules/assign-to-users/components/AssignToOtherUsersPanel/AssignToOtherUsersPanelView";
import trackingService from "app/pages/sales-intelligence/sub-modules/assign-to-users/services/tracking/trackingService";
import {
    assignLeadsToUserWithToastThunk,
    fetchAssignableUsersThunk,
} from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/effects";
import {
    selectAssignableUsers,
    selectAssignIsInProgress,
} from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/selectors";
import type { AssignableUser } from "app/pages/sales-intelligence/sub-modules/assign-to-users/types/common";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";

import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { COMPETITIVE_LANDSCAPE_TABLE_KEY, COMPETITIVE_LANDSCAPE_TABLE_PROPERTY } from "../../configuration/constants";
import useCompetitiveQueryParams from "../../hooks/useCompetitiveQueryParams";
import assignToUserSelectionDropdown from "../../models/assign-to-user/assignToUserSelectionDropdown";
import { tableRowsSelectionToggledAction } from "../../store/action-creators";
import { assignTopNDomainsToUserWithToastThunk } from "../../store/effects";
import { selectCheckedWebsites, selectCompetitiveResultsCount } from "../../store/selectors";

const AssignToUserCompetitivePanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const selectedWebsites = useSelector(selectCheckedWebsites);
    const numberOfResults = useSelector(selectCompetitiveResultsCount);
    const isAssignInProgress = useSelector(selectAssignIsInProgress);
    const users = useSelector(selectAssignableUsers);
    const { params } = useCompetitiveQueryParams();

    useEffect(() => {
        const ac = new AbortController();

        void dispatch(fetchAssignableUsersThunk({ cancellation: ac.signal }));

        return () => {
            ac.abort();
        };
    }, [dispatch]);

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection(
        tableRowsSelectionToggledAction,
        COMPETITIVE_LANDSCAPE_TABLE_KEY,
        COMPETITIVE_LANDSCAPE_TABLE_PROPERTY,
    );

    const handleDeactivate = () => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    function handleSelectionChange(item: Readonly<SelectionDropdownItem>) {
        if (item.id === ManualSelectionItem.ID) {
            return dispatch(tableRowsSelectionToggledAction(true));
        }

        clearAndCloseRowsSelection();
    }

    async function handleAssignSubmit(user: AssignableUser, selectionItem: Readonly<SelectionDropdownItem>) {
        let websites: string[] | null = null;

        if (selectionItem.id === ManualSelectionItem.ID) {
            websites = selectedWebsites;
            await dispatch(
                assignLeadsToUserWithToastThunk({
                    assigneeId: user.id,
                    domains: selectedWebsites,
                }),
            );
        } else {
            websites = await dispatch(assignTopNDomainsToUserWithToastThunk(selectionItem.value, user, params));
        }

        if (websites) {
            trackingService.trackUserSelected(websites.length, user.name);
        }

        handleDeactivate();
    }

    return (
        <AssignToOtherUsersPanelView
            {...props}
            selectionDropdown={assignToUserSelectionDropdown}
            isLoading={isAssignInProgress}
            numberOfResults={numberOfResults ?? 0}
            numberOfSelectedItems={selectedWebsites.length}
            users={users}
            onAssignSubmit={handleAssignSubmit}
            onSelectionChange={handleSelectionChange}
        />
    );
};

export default AssignToUserCompetitivePanelContainer;
